/**
 * Input + response contracts for the `reconcileEventAccess` callable
 * (Stage 2G safety net, updated for ADR-120 + ADR-137). Recomputes
 * `audiences.resolvedPersonIds` and the dependent
 * `sessions.targetedPersonIds` / `travelItems.targetedPersonIds` arrays
 * from source-of-truth `eventParticipants` for one event, writing deltas
 * via `arrayUnion` / `arrayRemove` only where drift exists.
 *
 * Used by:
 *   - The nightly `reconcileEventAccessCron` (which iterates all
 *     non-archived events and shares this core logic).
 *   - Manual admin "Recompute access for this event" affordance, when
 *     drift is suspected and an organizer wants to force a sweep without
 *     waiting for the cron.
 */

import { z } from 'zod';

export const reconcileEventAccessInputSchema = z
  .object({
    eventId: z.string().min(1),
  })
  .strict();

export type ReconcileEventAccessInput = z.infer<
  typeof reconcileEventAccessInputSchema
>;

export interface ReconcileEventAccessResponse {
  success: true;
  /** Number of `audiences` whose `resolvedPersonIds` was corrected. */
  audiencesCorrected: number;
  /** Number of sessions whose `targetedPersonIds` was corrected. */
  sessionsCorrected: number;
  /** Number of travelItems whose `targetedPersonIds` was corrected. */
  travelItemsCorrected: number;
  /** Total docs scanned across the three collections (audiences + sessions + travel). */
  docsScanned: number;
}
